import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";

import type { PlatformUserDetail } from "../api";
import { resourceHref } from "@/shared/resourceHref";

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

/** Recent user audit entries with links to recognized platform resources. */
export function UserActivityPanel({ user }: { user: PlatformUserDetail }) {
  return (
    <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
      <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
        Audit footprint
      </Typography>

      {user.recent_activity.length === 0 ? (
        <Typography color="text.secondary" variant="body2" sx={{ mt: 2 }}>
          No recent audit activity.
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />} spacing={1.5} sx={{ mt: 2 }}>
          {user.recent_activity.map((entry) => {
            const href = resourceHref(entry.resource_type, entry.resource_id);
            const resourceLabel = `${entry.resource_type}:${entry.resource_id}`;

            return (
              <Box key={entry.entry_id} sx={{ py: 0.5 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={0.5}
                  sx={{
                    alignItems: { xs: "flex-start", sm: "baseline" },
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {entry.action}
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    {formatDateTime(entry.created_at)}
                  </Typography>
                </Stack>
                {href ? (
                  <Link
                    component={RouterLink}
                    to={href}
                    variant="body2"
                    sx={{
                      display: "inline-block",
                      mt: 0.5,
                      wordBreak: "break-word",
                    }}
                  >
                    {resourceLabel}
                  </Link>
                ) : (
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{ mt: 0.5, wordBreak: "break-word" }}
                  >
                    {resourceLabel}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
